import { useState } from 'react';
import { Modal } from '../Modal';
import { Send, Users, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useEntityData } from '../../hooks/useSimulaData';

interface NotifyTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotifyTeamModal({ isOpen, onClose }: NotifyTeamModalProps) {
  const notifications = useEntityData('notifications');
  const users = useEntityData('users');
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    recipients: 'all' as 'all' | 'operators' | 'supervisors' | 'quality',
  });
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.message) {
      toast.error('Preencha o assunto e a mensagem');
      return;
    }

    setIsSending(true);

    try {
      const allUsers = (users.data || []) as any[];
      const audience = formData.recipients;
      const recipients = audience === 'all'
        ? []
        : allUsers.filter((u) => {
          if (audience === 'operators') return u.role === 'operador';
          if (audience === 'supervisors') return u.role === 'supervisor';
          if (audience === 'quality') return u.role === 'qualidade';
          return false;
        });

      if (audience === 'all') {
        await notifications.add({
          type: 'sistema',
          title: formData.subject,
          message: formData.message,
          date: new Date().toISOString(),
          isRead: false,
          priority: formData.priority === 'urgent' ? 'high' : formData.priority === 'normal' ? 'medium' : formData.priority,
          userId: null,
        } as any);
      } else {
        await Promise.all(recipients.map((recipient) => notifications.add({
          type: 'sistema',
          title: formData.subject,
          message: formData.message,
          date: new Date().toISOString(),
          isRead: false,
          priority: formData.priority === 'urgent' ? 'high' : formData.priority === 'normal' ? 'medium' : formData.priority,
          userId: recipient.id,
        } as any)));
      }

      toast.success('Notificação enviada com sucesso!', {
        description: `Mensagem enviada para ${getRecipientsLabel(formData.recipients)}.`,
      });
      setFormData({
        subject: '',
        message: '',
        priority: 'normal',
        recipients: 'all',
      });
      onClose();
    } catch (error) {
      console.error('[NotifyTeamModal] erro ao notificar:', error);
      toast.error('Falha ao enviar notificacao.');
    } finally {
      setIsSending(false);
    }
  };

  const getRecipientsLabel = (recipients: string) => {
    const labels: Record<string, string> = {
      all: 'toda a equipe',
      operators: 'todos os operadores',
      supervisors: 'todos os supervisores',
      quality: 'equipe de qualidade',
    };
    return labels[recipients] || 'destinatários selecionados';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'var(--muted-foreground)',
      normal: 'var(--primary)',
      high: 'var(--warning)',
      urgent: 'var(--destructive)',
    };
    return colors[priority];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Notificar Equipe"
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSending}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: 'var(--foreground)',
              cursor: isSending ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-medium)',
              opacity: isSending ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSending}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              border: 'none',
              cursor: isSending ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-semibold)',
              opacity: isSending ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Send size={18} />
            {isSending ? 'Enviando...' : 'Enviar Notificação'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Recipients */}
        <div>
          <label
            htmlFor="recipients"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Destinatários <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <Users
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
                pointerEvents: 'none',
              }}
            />
            <select
              id="recipients"
              value={formData.recipients}
              onChange={(e) => setFormData({ ...formData, recipients: e.target.value as any })}
              required
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
                cursor: 'pointer',
              }}
            >
              <option value="all">Toda a equipe</option>
              <option value="operators">Apenas operadores</option>
              <option value="supervisors">Apenas supervisores</option>
              <option value="quality">Equipe de qualidade</option>
            </select>
          </div>
        </div>

        {/* Priority */}
        <div>
          <label
            htmlFor="priority"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Prioridade <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <div className="grid grid-cols-4 gap-2">
            {(['low', 'normal', 'high', 'urgent'] as const).map((priority) => (
              <button
                key={priority}
                type="button"
                onClick={() => setFormData({ ...formData, priority })}
                style={{
                  padding: '8px',
                  borderRadius: 'var(--radius-button)',
                  border: `2px solid ${formData.priority === priority ? getPriorityColor(priority) : 'var(--border)'}`,
                  backgroundColor: formData.priority === priority ? `${getPriorityColor(priority)}10` : 'transparent',
                  color: formData.priority === priority ? getPriorityColor(priority) : 'var(--foreground)',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 'var(--text-sm)',
                  textTransform: 'capitalize',
                }}
              >
                {priority === 'low' && '🔵'}
                {priority === 'normal' && '⚪'}
                {priority === 'high' && '🟠'}
                {priority === 'urgent' && '🔴'}
                <br />
                {priority === 'low' && 'Baixa'}
                {priority === 'normal' && 'Normal'}
                {priority === 'high' && 'Alta'}
                {priority === 'urgent' && 'Urgente'}
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label
            htmlFor="subject"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Assunto <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <input
            id="subject"
            type="text"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Ex: Atualização importante sobre processos"
            required
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--background)',
              color: 'var(--foreground)',
              fontSize: 'var(--text-base)',
            }}
          />
        </div>

        {/* Message */}
        <div>
          <label
            htmlFor="message"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Mensagem <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Digite sua mensagem aqui..."
            rows={6}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--background)',
              color: 'var(--foreground)',
              fontSize: 'var(--text-base)',
              resize: 'vertical',
            }}
          />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginTop: '4px' }}>
            {formData.message.length} caracteres
          </p>
        </div>

        {/* Preview */}
        {formData.subject && formData.message && (
          <div
            style={{
              backgroundColor: 'var(--muted)',
              padding: '16px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Info size={18} style={{ color: 'var(--primary)' }} />
              <p style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)' }}>
                Pré-visualização
              </p>
            </div>
            <div
              style={{
                backgroundColor: 'var(--card)',
                padding: '12px',
                borderRadius: 'var(--radius)',
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <p style={{ fontWeight: 'var(--font-weight-semibold)' }}>{formData.subject}</p>
                <div
                  style={{
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-button)',
                    backgroundColor: `${getPriorityColor(formData.priority)}20`,
                    color: getPriorityColor(formData.priority),
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    textTransform: 'uppercase',
                  }}
                >
                  {formData.priority}
                </div>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
                {formData.message}
              </p>
            </div>
          </div>
        )}

        {/* Info */}
        <div
          style={{
            backgroundColor: 'var(--muted)',
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <CheckCircle2 size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
            A notificação será enviada por email e aparecerá no centro de notificações do sistema.
          </p>
        </div>
      </form>
    </Modal>
  );
}
